import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { aciesDateFormat } from '@shared/helpers';
import { IBase, IUpdated } from '@shared/models/api/base';
import { GenericTableColumn, ISelectableData } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { CrudService } from '@shared/services/crud.service';
import { PalletisingPalletTransferService } from '@shared/services/export-import-services/palletisingPalletTransfer.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IPallet, IUIPallet, IZipCode, PalletisingService } from '@shared/services/palletising.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { SelectSubMenuItem } from '@shared/utils/SelectSubMenuItemManager';
import { catchError, combineLatest, forkJoin, Subject, takeUntil, throwError } from 'rxjs';
import { CreateUpdatePalletsDialogComponent, IDialogData } from './create-update-pallets/create-update-pallets';

interface IAction {
	title: string;
	handler: 'edit' | 'delete';
}

interface IRowAction {
	action: IAction;
	data: ISelectableData<IUIPallet>;
}

@Component({
	selector: 'app-pallets',
	templateUrl: './pallets.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PalletsComponent implements OnInit, OnDestroy {
	public unsubscribe: Subject<void> = new Subject<void>();
	public isLoading: Subject<boolean> = new Subject<boolean>();
	public searchFilter: string = '';
	public count: Subject<number> = new Subject<number>();
	private filteredPallets: IUIPallet[];
	private allPallets: IUIPallet[];
	public tableSource: MatTableDataSource<IUIPallet, MatPaginator> = new MatTableDataSource<IUIPallet>();

	public activeOnly: boolean;
	public dimensions: IBase[] = [];
	public types: IBase[] = [];
	public rules: number[] = [];
	private dimensionsLookup: Map<string, IBase | undefined> = new Map<string, IBase | undefined>();

	private typesLookup: Map<string, IBase | undefined> = new Map<string, IBase | undefined>();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.palletising.pallets.name', dataKey: 'name' },
		{
			name: 'masterdata.palletising.pallets.description',
			dataKey: 'description',
		},
		{
			name: 'masterdata.palletising.pallets.dialog.status.label',
			dataKey: 'activeLabel',
		},
		{
			name: 'masterdata.palletising.pallets.dialog.length.label',
			dataKey: 'length',
		},
		{
			name: 'masterdata.palletising.pallets.dialog.dimension',
			dataKey: 'dimensionName',
		},
		{
			name: 'masterdata.palletising.pallets.dialog.type',
			dataKey: 'typeName',
		},
		{
			name: 'masterdata.palletising.pallets.dialog.zipCodeRule',
			dataKey: 'zipCodeRule',
		},
		{ name: 'masterdata.updatedDate', dataKey: 'updatedAt', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: IAction[] = [
		{ title: 'masterdata.edit', handler: 'edit' },
		{ title: 'masterdata.delete', handler: 'delete' },
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.palletising.pallets.create',
			click: () => this.openDialog(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.palletising.pallets.import.button',
			click: () => this.onImport(),
		},
	];

	selectActions: SelectSubMenuItem<IUIPallet>[] = [
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.palletising.pallets.export.button',
			click: (pallets) => this.onExport(pallets),
		},
	];

	public constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private sessionStorageService: SessionStorageService,
		private crudService: CrudService,
		private notificationService: NotificationService,
		private palletisingService: PalletisingService,
		private palletTransferService: PalletisingPalletTransferService,
		private injector: Injector,
	) {}

	ngOnInit(): void {
		this.isLoading.next(true);

		this.loadData();
	}

	public loadData(): void {
		forkJoin([this.crudService.get<{ items: IBase[] }>('/palletising/palletdimensions'), this.crudService.get<{ items: IBase[] }>('/palletising/pallettypes'), this.palletisingService.fetchZipCodes()])
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: ([dimensionsData, typesData, zipCodes]) => {
					this.updateDimensions(dimensionsData.items);
					this.updateTypes(typesData.items);
					this.updateRules(zipCodes.items);
					this.updateLookupMaps();
					this.loadPallets();
					this.listenForLanguageChangeAndLoadData();
					this.isLoading.next(false);
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	private listenForLanguageChangeAndLoadData(): void {
		this.sessionStorageService.watch('User.PreferredLanguage').subscribe(() => {
			this.translateData();
		});
	}

	private translateData() {
		setTimeout(() => {
			const pallets = this.tableSource.data;
			pallets.forEach((e) => {
				const label = e.active ? this.translator.instant('masterdata.palletising.pallets.enabled-status') : this.translator.instant('masterdata.palletising.pallets.disabled-status');
				e.activeLabel = label;
			});
			this.tableSource.data = pallets;
		}, 10);
	}

	public loadDimensionsAndTypes(): void {
		combineLatest([this.crudService.get<{ items: IBase[] }>('/palletising/palletdimensions'), this.crudService.get<{ items: IBase[] }>('/palletising/pallettypes'), this.palletisingService.fetchZipCodes()])
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (data) => {
					this.updateDimensions(data[0].items);
					this.updateTypes(data[1].items);
					this.updateRules(data[2].items);
					this.updateLookupMaps();
					this.loadPallets();
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	private updateDimensions(dimensions: IBase[]): void {
		// empty the Array, since re assigning a new Array will break the Angular reactivity
		this.dimensions.splice(0, this.dimensions.length);
		this.dimensions.push(...dimensions);
	}

	private updateRules(zipCodes: IZipCode[]): void {
		// empty the Array, since re assigning a new Array will break the Angular reactivity
		this.rules.splice(0, this.dimensions.length);

		const rulesSet: Set<number> = zipCodes.reduce((acc, zipCode) => {
			acc.add(zipCode.ruleNumber);

			return acc;
		}, new Set<number>());

		const rules = Array.from(rulesSet).sort((a, b) => a - b);

		this.rules.push(...rules);
	}

	private updateTypes(types: IBase[]): void {
		// empty the Array, since re assigning a new Array will break the Angular reactivity
		this.types.splice(0, this.types.length);
		this.types.push(...types);
	}

	private updateLookupMaps(): void {
		for (const dimension of this.dimensions) {
			this.dimensionsLookup.set(dimension.self, dimension);
		}
		for (const type of this.types) {
			this.typesLookup.set(type.self, type);
		}
	}

	public loadPallets(): void {
		this.crudService
			.get<{ items: IPallet[] }>('/palletising/pallets')
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (data) => {
					this.tableSource.data = data.items.map((value) => this.iPalletToIUIPalletAdapter(value));
					this.count.next(data.items.length);
					this.isLoading.next(false);
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	private sortPalletsByName(a: IPallet, b: IPallet): -1 | 1 {
		return a.name < b.name ? -1 : 1;
	}

	handleRowMenuAction(object: object): void {
		const row = object as IRowAction;
		if (row.action.handler === 'edit') {
			this.openDialog(row.data);
		} else {
			this.onDeleteItem(row.data);
		}
	}

	public applyActiveOnlyFilter() {
		this.activeOnly = !this.activeOnly;
		if (this.activeOnly) {
			this.allPallets = this.tableSource.data;
			this.filteredPallets = this.tableSource.data.filter((e) => e.active === true);
			this.tableSource.data = this.filteredPallets;
		} else {
			this.tableSource.data = this.allPallets;
		}
	}

	public openDialog(pallet: ISelectableData<IUIPallet> | null = null): void {
		const data: IDialogData = {
			pallet,
			dimensions: this.dimensions,
			types: this.types,
			rules: this.rules,
		};
		this.dialog
			.open(CreateUpdatePalletsDialogComponent, {
				disableClose: true,
				width: '650px',
				data,
			})
			.componentInstance.saved.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadPallets();
			});
	}

	public onDeleteItem(pallet: IUIPallet) {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('masterdata.palletising.pallets.delete.heading'),
					content: this.translator.instant('masterdata.palletising.pallets.delete.content', { name: pallet.name }),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.tableSource.data = [];
					this.deletePallet(pallet);
				}
			});
	}

	public deletePallet(pallet: IUIPallet): void {
		this.palletisingService
			.deletePallet(pallet)
			.pipe(catchError(this.handleError), takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.palletising.pallets.notification.header'], ['masterdata.palletising.pallets.notification.content', { name: pallet.name }], 'success');
					this.loadData();
				},
				error: (error: Error) => {
					this.notificationService.notify([error.message], [], 'error');
				},
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			return throwError(() => new Error('masterdata.palletising.errors.network'));
		}
		return throwError(() => new Error('masterdata.palletising.errors.unknown'));
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	private getUpdatedByFromMeta(meta: IUpdated): string {
		const segments = meta.updatedBy.self.split('/');
		const uid = segments.at(-1);
		return uid;
	}

	private iPalletToIUIPalletAdapter({ configuration, description, dimension, meta, name, self, type, zipCodeRule }: IPallet): ISelectableData<IUIPallet> {
		const label = configuration.active ? this.translator.instant('masterdata.palletising.pallets.enabled-status') : this.translator.instant('masterdata.palletising.pallets.disabled-status');
		return {
			id: self,
			description,
			name,
			self,
			updatedAt: aciesDateFormat(new Date(meta.updatedAt), true, false),
			updatedAtEpoch: meta.updatedAt,
			updatedBy: this.getUpdatedByFromMeta(meta),
			activeLabel: label,
			active: configuration.active,
			dimension: dimension ? this.dimensionsLookup.get(dimension.self) : null,
			dimensionName: dimension ? this.dimensionsLookup.get(dimension.self).name : null,
			height: configuration.height,
			length: configuration.length,
			maxHeight: configuration.maxHeight,
			maxLayers: configuration.maxLayers,
			maxWeight: configuration.maxWeight,
			oversize: configuration.oversize,
			spaceBetween: configuration.spaceBetween,
			type: type ? this.typesLookup.get(type.self) : null,
			typeName: type ? this.typesLookup.get(type.self).name : '',
			zipCodeRule,
			weight: configuration.weight,
			width: configuration.width,
		};
	}

	onExport(pallets: IUIPallet[]) {
		this.palletTransferService.exportData(pallets.map(this.mapUIToPallet)).subscribe({
			next: () => {
				this.notificationService.notify(['masterdata.palletising.pallets.export.notification.success.header'], ['masterdata.palletising.pallets.export.notification.success.content'], 'success');
			},
			error: (error) => {
				console.error('Failed to export data', error);
				this.notificationService.notify(['masterdata.palletising.pallets.export.notification.error.header'], ['masterdata.palletising.pallets.export.notification.error.content'], 'error');
			},
		});
	}

	mapUIToPallet(pallet: IUIPallet): IPallet {
		return {
			description: pallet.description,
			name: pallet.name,
			self: pallet.self,
			configuration: {
				active: pallet.active,
				height: pallet.height,
				length: pallet.length,
				maxHeight: pallet.maxHeight,
				maxLayers: pallet.maxLayers,
				maxWeight: pallet.maxWeight,
				oversize: pallet.oversize,
				spaceBetween: pallet.spaceBetween,
				weight: pallet.weight,
				width: pallet.width,
			},
			dimension: pallet.dimension ? { self: pallet.dimension.self } : null,
			type: pallet.type ? { self: pallet.type.self } : null,
			zipCodeRule: pallet.zipCodeRule,
			meta: null,
		};
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.palletising.pallets.import.title',
				subHeadingKey: 'masterdata.palletising.pallets.import.subtitle',
				confirmButtonKey: 'masterdata.palletising.pallets.import.button',
				notificationHeader: 'masterdata.palletising.pallets.import.notification.success.header',
				notificationContent: 'masterdata.palletising.pallets.import.notification.success.content',
				notificationErrorHeader: 'masterdata.palletising.pallets.import.notification.error.header',
				notificationErrorContent: 'masterdata.palletising.pallets.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.palletising.pallets.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.palletising.pallets.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.palletTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'masterdata.palletising.pallets.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.loadPallets();
		});
	}
}
